import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { calculateReservationTotal } from '../calculateReservationTotal'
import { loadCheckoutRecovery } from './loadCheckoutRecovery'
import { RecoveryDataError } from './recoveryData'
import { ReservationApiError, reservationApi } from '../reservationApi'
import { buildCheckoutRecoveryReservationPayload } from './buildCheckoutRecoveryReservationPayload'
import { usePromotionCodes } from '../promotions/usePromotionCodes'

export const useCheckoutRecovery = (
    bootstrap,
    api = reservationApi,
) => {
    const { t } = useTranslation()

    const [state, setState] = useState({
        loading: true,
        data: null,
        error: null,
        errors: {},
    })

    const [reservationLoading, setReservationLoading] = useState(false)
    const [ignoreSavedPromotionCodes, setIgnoreSavedPromotionCodes] =
        useState(false)

    useEffect(() => {
        let active = true

        const initialize = async () => {
            if (
                !bootstrap ||
                !bootstrap.enabled ||
                bootstrap.error ||
                !bootstrap.data
            ) {
                setState({
                    loading: false,
                    data: null,
                    error: 'recovery.invalidData',
                    errors: {},
                })

                return
            }

            setState({
                loading: true,
                data: null,
                error: null,
                errors: {},
            })

            try {
                const recovery = ignoreSavedPromotionCodes
                    ? {
                        ...bootstrap.data,
                        choices: {
                            ...bootstrap.data.choices,
                            promotionCodes: [],
                        },
                    }
                    : bootstrap.data

                const data = await loadCheckoutRecovery(
                    recovery,
                    api,
                )

                if (!active) {
                    return
                }

                setState({
                    loading: false,
                    data,
                    error: null,
                    errors: {},
                })
            } catch (error) {
                if (!active) {
                    return
                }

                setState({
                    loading: false,
                    data: null,
                    error: error instanceof RecoveryDataError
                        ? error.code
                        : 'recovery.requestFailed',
                    errors: {},
                })
            }
        }

        initialize()

        return () => {
            active = false
        }
    }, [bootstrap, api, ignoreSavedPromotionCodes])

    const setPaymentMethod = (paymentMethod) => {
        setState((previous) => {
            if (!previous.data) {
                return previous
            }

            return {
                ...previous,
                errors: {},
                data: {
                    ...previous.data,
                    paymentMethod,
                },
            }
        })
    }

    const updateAgreements = (update) => {
        setState((previous) => {
            if (!previous.data) {
                return previous
            }

            return {
                ...previous,
                data: {
                    ...previous.data,
                    form: {
                        ...previous.data.form,
                        agreements: update(
                            previous.data.form.agreements,
                        ),
                    },
                },
            }
        })
    }

    const handleAgreementChange = (index, checked) => {
        updateAgreements((agreements) =>
            agreements.map((agreement, currentIndex) =>
                currentIndex === index
                    ? { ...agreement, checked: Boolean(checked) }
                    : agreement,
            ),
        )
    }

    const handleMasterCheckboxChange = () => {
        updateAgreements((agreements) => {
            const allChecked =
                agreements.length > 0 &&
                agreements.every((agreement) => agreement.checked)

            return agreements.map((agreement) => ({
                ...agreement,
                checked: !allChecked,
            }))
        })
    }

    const calculateRecoveryPromotionsPreview = async (
        nextPromotionCodes,
    ) => {
        if (!state.data) {
            return {
                status: false,
                message: t('promotion-validation-error'),
            }
        }

        const promotions = await api.calculatePromotions({
            ...state.data.request,
            promotionCodes: nextPromotionCodes,
        })

        return {
            status: true,
            promotions,
        }
    }

    const updateRecoveryPromotions = (
        promotionCodes,
        promotionPreview,
    ) => {
        setState((previous) => {
            if (!previous.data) {
                return previous
            }

            return {
                ...previous,
                data: {
                    ...previous.data,
                    promotionCodes,
                    promotionPreview,
                },
            }
        })
    }

    const {
        promotionCodeInput,
        setPromotionCodeInput,
        promotionError,
        addPromotionCode,
        removePromotionCode,
    } = usePromotionCodes({
        promotionCodes: state.data
            ? state.data.promotionCodes
            : [],
        calculatePreview: calculateRecoveryPromotionsPreview,
        onChange: updateRecoveryPromotions,
        t,
    })

    const handleSubmit = async () => {
        if (!state.data || reservationLoading) {
            return
        }

        setState((previous) => ({
            ...previous,
            errors: {},
        }))

        setReservationLoading(true)

        try {
            const response = await api.makeReservation(
                buildCheckoutRecoveryReservationPayload(state.data)
            )

            if (
                !response ||
                typeof response.paymentUrl !== 'string' ||
                response.paymentUrl === ''
            ) {
                throw new ReservationApiError(200)
            }

            window.location.href = response.paymentUrl
        } catch (error) {
            const errors =
                error instanceof ReservationApiError &&
                error.errors
                    ? error.errors
                    : {
                        recovery: [
                            t('recovery.requestFailed'),
                        ],
                    }

            setState((previous) => ({
                ...previous,
                errors,
            }))
        } finally {
            setReservationLoading(false)
        }
    }

    const getReservationTotalPrice = (
        includeCashbackDiscount = false,
    ) => calculateReservationTotal(
        state.data || {},
        includeCashbackDiscount,
    )

    const agreements = state.data
        ? state.data.form.agreements
        : []

    return {
        ...(state.data || {}),
        isRecovery: true,
        loading: state.loading,
        recoveryError: state.error,
        retryWithoutPromotionCodes: () => {
            setIgnoreSavedPromotionCodes(true)
        },
        recoveryPrice: state.data ? state.data.price : null,
        recoveryFallbackUrl:
            bootstrap && bootstrap.data
                ? bootstrap.data.fallbackUrl
                : null,
        errors: state.errors,
        reservationLoading,
        masterCheckbox:
            agreements.length > 0 &&
            agreements.every((agreement) => agreement.checked),
        setPaymentMethod,
        handleAgreementChange,
        handleMasterCheckboxChange,
        handleSubmit,
        getReservationTotalPrice,
        promotionCodeInput,
        setPromotionCodeInput,
        promotionError,
        addPromotionCode,
        removePromotionCode,
    }
}
